import { FloatingMessagingThreadWindow, FloatingMessagingThreadWindowProps } from '../FloatingMessagingThreadWindow';
import { VirtualMessageThreadContainer } from '../VirtualMessageThreadContainer';
import { VirtualMessageThreadItemContainer } from '../VirtualMessageThreadItemContainer';
import { ConversationThreadMessage } from '../ConversationThreadMessage';
import { VirtualMessageThreadDateDivider } from '../VirtualMessageThreadDateDivider';
import { CreateMessageForm, CreateMessageFormProps } from './components';
import { useAdvisorFloatingThreadWindow, UseAdvisorFloatingThreadWindowArgs } from './hooks';
import { Trans } from 'react-i18next';
import { Link } from '@remix-run/react';
import { ConversationParticipantType } from '../../../generated/graphql';
import { AdvisorMessageThreadOnboardingReminder } from '../AdvisorMessageThreadOnboardingReminder';

interface AdvisorFloatingMessagingThreadWindowProps
  extends UseAdvisorFloatingThreadWindowArgs,
    Pick<FloatingMessagingThreadWindowProps, 'close' | 'dragConstraintsAdjustments'> {
  firmID: string;
  firmClientID: string;
  routes: { messagingQuickCheckIn: string };
  messageActionMenuProps: Pick<CreateMessageFormProps['messageActionMenuProps'], 'visibilityOptions' | 'routes'>;
}

export function AdvisorFloatingMessagingThreadWindow({
  close,
  routes,
  firmID,
  firmClientID,
  firmMemberForSelfID,
  messageActionMenuProps,
  dragConstraintsAdjustments,
}: AdvisorFloatingMessagingThreadWindowProps) {
  const {
    tMessaging,
    expandedTagListID,
    footerRef,
    scrollContainerRef,
    threadVirtualizer,
    virtualizerItems,
    conversation,
    createMessage,
    fileListShiftOverride,
    self,
    setExpandedTagListID,
    useFormReturn,
    isNewConversation,
    firmClient,
    isLoading,
    firmMemberForSelf,
    handleSendCalendarSchedulingLink,
  } = useAdvisorFloatingThreadWindow({ firmClientID, firmID, firmMemberForSelfID });

  return (
    <FloatingMessagingThreadWindow
      close={close}
      isLoading={isLoading}
      footerRef={footerRef}
      scrollContainerRef={scrollContainerRef}
      disableScroll={expandedTagListID !== undefined}
      dragConstraintsAdjustments={dragConstraintsAdjustments}
      body={
        isNewConversation ? (
          <div className="grid place-content-center w-full h-full">
            <p className="w-[280px] justify-between justify-self-center whitespace-pre-line text-center">
              <Trans
                ns="inAppMessaging"
                i18nKey="compose-view-description"
                components={{ a: <Link className="text-palette-600" to={routes.messagingQuickCheckIn} /> }}
              />
            </p>
          </div>
        ) : (
          <VirtualMessageThreadContainer virtualizer={threadVirtualizer} virtualizerItems={virtualizerItems}>
            <VirtualMessageThreadItemContainer measureElement={threadVirtualizer.measureElement}>
              {({ message, dateDivider }) => (
                <>
                  {message && (
                    <ConversationThreadMessage
                      self={self}
                      message={message}
                      className="px-6 w-full m-auto"
                      conversation={conversation}
                      isScrollingMessageThread={threadVirtualizer.isScrolling}
                      linkifyAnchorRenderer={({ attributes }) =>
                        attributes?.href === firmMemberForSelf?.calendarSchedulingLink && (
                          <a {...attributes}>{tMessaging('calendar-scheduling-link-anchor-text')}</a>
                        )
                      }
                      onboardingReminderElement={<AdvisorMessageThreadOnboardingReminder />}
                      optimisticSelfParticipantType={ConversationParticipantType.FirmMember}
                      messageExpandableTagListProps={{
                        expandedTagListID,
                        setExpandedTagListID,
                        floatingOverrides: { shiftMiddleware: fileListShiftOverride },
                      }}
                    />
                  )}
                  {dateDivider && <VirtualMessageThreadDateDivider className="!px-6" date={dateDivider} />}
                </>
              )}
            </VirtualMessageThreadItemContainer>
          </VirtualMessageThreadContainer>
        )
      }
      footer={
        conversation && (
          <CreateMessageForm
            firmClient={firmClient}
            conversation={conversation}
            useFormReturn={useFormReturn}
            isNewConversation={isNewConversation}
            createFileTenantID={conversation?.id}
            isCreateMessageLoading={createMessage.isLoading}
            isScrollingMessageThread={threadVirtualizer.isScrolling}
            messageActionMenuProps={{
              ...messageActionMenuProps,
              visibilityOptions: {
                ...messageActionMenuProps.visibilityOptions,
                hideSendCalendarSchedulingLink: !firmMemberForSelf?.calendarSchedulingLink,
              },
              clickHandlers: {
                onOnboardingReminder: () => useFormReturn.formMethods.setValue('hasOnboardingReminder', true),
                onSendCalendarSchedulingLink: handleSendCalendarSchedulingLink,
              },
            }}
            messageExpandableTagListProps={{
              expandedTagListID,
              setExpandedTagListID,
              floatingOverrides: { offsetMiddleware: { crossAxis: 32 } },
            }}
          />
        )
      }
    />
  );
}
